import type { ViewJobVariant } from "../ViewJobMainContent/ViewJobMainContent";

import { CTAEnticerBannerContent } from "./CTAEnticerBannerContent";
import { WithOptionalCTAEnticerBannerUI } from "./WithOptionalCTAEnticerBannerUI";
import { logScreenView } from "../../../../../../shared/native-modules/tare/logScreenView";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import { useEmployerOutreachSource } from "../../../../hooks/useEmployerOutreachSource";
import {
  CAREER_SCOUT_ALWAYS_AWARE_BUCKETS,
  useCareerScoutAlwaysAwareBucket,
} from "../../AskCareerScoutStarters/proctor";
import { EmployerOutreachEnticerContent } from "../EmployerOutreachEnticer";
import { InterviewNowCTAEnticerBannerContent } from "../InterviewNow/InterviewNowCTAEnticerBannerContent";
import { useInterviewNow } from "../InterviewNow/useInterviewNow";

import React, { type PropsWithChildren } from "react";

export type WithOptionalCTAEnticerBannerProps = PropsWithChildren & {
  hasLoggedImpression: React.RefObject<boolean>;
  jobKey?: string;
  variant: ViewJobVariant;
};

export const WithOptionalCTAEnticerBanner: React.FC<WithOptionalCTAEnticerBannerProps> = ({
  children,
  hasLoggedImpression,
  jobKey,
  variant: _variant,
}) => {
  const interviewNowModel = useInterviewNow();
  const { isFromEmployerOutreach } = useEmployerOutreachSource();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const isScrollVariant =
    useCareerScoutAlwaysAwareBucket() === CAREER_SCOUT_ALWAYS_AWARE_BUCKETS.SCROLL;

  const getOptionalContent = () => {
    const iNowInterviewerCount = interviewNowModel?.interviewerCount;

    if (iNowInterviewerCount && !isScrollVariant) {
      // Note: InterviewNow enticer must have precedence over other enticers.
      // The interviewNowModel is only present for Indeed Apply Jobs.
      return (
        <InterviewNowCTAEnticerBannerContent
          interviewerCount={iNowInterviewerCount}
          hasLoggedImpression={hasLoggedImpression}
          onFirstImpression={() => {
            logScreenView("rn-view-job-interview-now-cta-enticer-seen").catch(console.error);
          }}
        />
      );
    }

    if (isFromEmployerOutreach && jobKey) {
      return <EmployerOutreachEnticerContent jobKey={jobKey} />;
    }

    const ctaEnticer = viewjobOneGraphModel?.ctaEnticer;

    if (ctaEnticer && !isScrollVariant) {
      return (
        <CTAEnticerBannerContent
          text={ctaEnticer}
          onFirstImpression={() => {
            logScreenView("rn-view-job-cta-enticer-seen").catch(console.error);
          }}
          hasLoggedImpression={hasLoggedImpression}
        />
      );
    }
  };

  return (
    <WithOptionalCTAEnticerBannerUI optionalContent={getOptionalContent()}>
      {children}
    </WithOptionalCTAEnticerBannerUI>
  );
};
